import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormField from "shared/form/FormField";
import {useNavigate} from "react-router-dom";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {useAuth} from "hooks/useAuth";
import {APIPostLogin} from "../api/login";
import {APIGetSchoolsLogin} from "../api/schools";
import {useServerErrors} from "../../../hooks/useServerErrors";
export interface IFormLogin {
    domain: {label: string, value: number, slug: string};
    email: string;
    password: string;
}

const schema = yup
    .object({})
    .required();

const LoginForm = ({type} : {type: "school" | "teachers"}) => {
    const {login, update} = useAuth();
    const navigate = useNavigate();

    const methods = useForm<IFormLogin>({
        resolver: yupResolver(schema),
    });

    const { handleServerErrors } = useServerErrors<IFormLogin>(methods.setError);

    const mapUserLogin = (auth: any) => {
        const baseUser = {
            firstname: auth.first_name || "",
            lastname: auth.last_name || "",
            email: auth.email || "",
            position: auth.position || "",
            profilePhoto: auth.profile_photo || "",
            token: `Bearer ${auth.token}`,
            menu: auth.menu || [],
            id: auth.id,
            notifications: auth.notifications_unread,
            emailVerified: auth.email_verified,
            date: new Date(),
            tenantNotifications: auth?.tenant_notifications ?? false,
            emailNotifications: auth?.email_notifications ?? false,
            testShareLink: auth.test_share_link ?? false,
        };

        if (type === "school") {
            return {
                ...baseUser,
                schoolBoard: auth.has_school_board,
                swap: auth.can_swap_profile,
                testsListingShow: auth?.tests_listing_show_report_and_score ?? true,
            };
        }

        return {
            ...baseUser,
            email_verified: auth.email_verified,
        };
    };

    const handleLogin = (apiResponse: any) => {
        const authData = apiResponse.data.data.auth;
        login(mapUserLogin(authData));

        const { label, value, ...tenant } = apiResponse.data.data.domain;
        update("tenant", tenant);
    };

    const handleLoginError = (err: any) => {
        if (err?.response?.status === 400) {
            const errors = err.response.data.data;
            handleServerErrors(errors);
            if (errors.general) {
                methods.setError("password", {type: "server", message: errors.general[0]});
            }
        } else {
            update("error", {show: true, code: "A001"});
        }
    }

    const onSubmit: SubmitHandler<IFormLogin> = (data) => {
        const domain = type === "school" ? (data.domain?.slug || null) : "teachers";

        update("loading", {show: true, modal: true});
        APIPostLogin({
            domain: domain,
            email: data.email,
            password: data.password,
        })
            .then((response) => {
                if (response.data.success && response.data.data?.token !== null) {
                    // console.log("postLOGIN OK", response);
                    methods.clearErrors();
                    if (response.data.data?.email_verified === null) {
                        navigate("/verifica-email");
                    } else {
                        handleLogin(response);
                        if (response.data.data.auth?.can_swap_profile && response.data.data.auth?.position === "principal") {
                            navigate("/profile");
                        }
                    }
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(handleLoginError)
            .finally(() => update("loading", {show: false}));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                {type === "school" &&
                    <Box py={1}>
                        <FormField
                            name="domain"
                            type="autocomplete_api"
                            label="Tastează numele școlii"
                            optionsAPI={APIGetSchoolsLogin}
                            defaultValue={null}
                            multiple={false}
                            filterOptions={(x: any) => x}
                            errorMessage={methods.formState.errors.domain?.message}
                            error={!!methods.formState.errors.domain?.message}
                            autoFocus={true}
                        />
                    </Box>
                }
                <Box py={1}>
                    <FormField
                        name="email"
                        type="text"
                        defaultValue=""
                        label="Adresa de e-mail"
                        autoComplete="email"
                        errorMessage={methods.formState.errors.email?.message}
                        inputProps={{id: 'login-email', autoCapitalize: 'none'}}
                    />
                </Box>
                <Box py={1}>
                    <FormField
                        name="password"
                        type="password"
                        defaultValue=""
                        label="Parola"
                        autoComplete="password"
                        errorMessage={methods.formState.errors.password?.message}
                        inputProps={{id: 'login-password'}}
                    />
                </Box>
                <Box sx={{mt: 1, mb: 3}}>
                    <Button type="submit" variant="contained">Autentificare</Button>
                </Box>
            </form>
        </FormProvider>
    );
};

export default LoginForm;
